import {
    BrainCircuit,
    CheckCircle2,
    Clock,
    FileSearch,
    FileText,
    Link2,
    Loader2,
    SearchCode,
    XCircle,
} from 'lucide-vue-next';

export type ActivityStatus =
    | 'pending'
    | 'running'
    | 'completing'
    | 'completed'
    | 'failed'
    | 'cancelled';
export type ActivityType =
    | 'screening'
    | 'extraction'
    | 'search_equation'
    | 'pdf_matching'
    | 'zotero_import';
export type BadgeTone = 'default' | 'secondary' | 'destructive' | 'outline';

/**
 * Returns the human-friendly French label for a given status,
 * optionally refined by the activity type (e.g. "import en cours" for zotero_import).
 */
export function getStatusLabel(
    status: ActivityStatus,
    type?: ActivityType,
): string {
    if (status === 'running') {
        if (type === 'zotero_import') {
            return 'import en cours';
        }

        if (type === 'pdf_matching') {
            return 'rattachement en cours';
        }

        return 'analyse en cours';
    }

    const labels: Record<ActivityStatus, string> = {
        pending: 'en attente',
        completing: 'finalisation',
        completed: 'terminé',
        failed: 'erreur',
        cancelled: 'interrompu',
        running: 'analyse en cours',
    };

    return labels[status] ?? status;
}

/** Returns the Badge variant to use for the given status. */
export function getStatusTone(status: ActivityStatus): BadgeTone {
    const tones: Record<ActivityStatus, BadgeTone> = {
        pending: 'secondary',
        running: 'default',
        completing: 'default',
        completed: 'outline',
        failed: 'destructive',
        cancelled: 'outline',
    };

    return tones[status] ?? 'secondary';
}

/** Returns the Lucide icon component for the given status. */
export function getStatusIcon(status: ActivityStatus) {
    const icons: Record<ActivityStatus, typeof Loader2> = {
        pending: Clock,
        running: Loader2,
        completing: Loader2,
        completed: CheckCircle2,
        failed: XCircle,
        cancelled: XCircle,
    };

    return icons[status] ?? Clock;
}

/** Whether the status icon should animate (spin). */
export function isStatusAnimated(status: ActivityStatus): boolean {
    return status === 'running' || status === 'completing';
}

/** Returns the human-friendly French label for an activity type. */
export function getTypeLabel(type: ActivityType | string): string {
    const labels: Record<string, string> = {
        screening: 'Sélection des références',
        extraction: 'Extraction des données',
        search_equation: 'Équation de recherche',
        pdf_matching: 'Rattachement des textes intégraux',
        zotero_import: 'Import Zotero',
    };

    return labels[type] ?? type;
}

/** Returns the Lucide icon component for an activity type. */
export function getTypeIcon(type: ActivityType | string) {
    const icons: Record<string, typeof BrainCircuit> = {
        screening: BrainCircuit,
        extraction: FileText,
        search_equation: SearchCode,
        pdf_matching: Link2,
        zotero_import: FileSearch,
    };

    return icons[type] ?? BrainCircuit;
}

/** Returns a friendly French label for a raw RunEvent type string. */
export function getEventLabel(eventType: string): string {
    const labels: Record<string, string> = {
        step_started: 'Étape démarrée',
        step_completed: 'Étape terminée',
        step_failed: 'Étape en erreur',
        paper_processed: 'Référence traitée',
        paper_included: 'Référence incluse',
        paper_excluded: 'Référence exclue',
        screening_started: 'Sélection démarrée',
        screening_completed: 'Sélection terminée',
        extraction_started: 'Extraction démarrée',
        extraction_completed: 'Extraction terminée',
        import_started: 'Import démarré',
        import_completed: 'Import terminé',
        matching_started: 'Rattachement démarré',
        matching_completed: 'Rattachement terminé',
        RunProgressUpdated: 'Progression mise à jour',
        RunStatusChanged: 'Statut mis à jour',
        RunCompleted: 'Activité terminée',
        RunFailed: 'Activité en erreur',
    };

    return labels[eventType] ?? 'Étape franchie';
}

/** Whether a status is considered active (can be interrupted). */
export function isActiveStatus(status: ActivityStatus): boolean {
    return ['pending', 'running', 'completing'].includes(status);
}
